import { useEffect, useRef, useState, useCallback } from 'react';
import { Upload, X, Plus, Download, Image as ImageIcon } from 'lucide-react';

// --------------------------------------------------------------------------
// Export presets. Each preset's larger side is 4096px (the informal "4K"
// baseline for social graphics) with the exact aspect ratio preserved.
// --------------------------------------------------------------------------
const FORMATS = {
  square: { w: 4096, h: 4096, label: 'Square · 1:1' },
  post: { w: 4096, h: 5120, label: 'Post · 4:5' },
  story: { w: 2304, h: 4096, label: 'Story · 9:16' },
};

const TEMPLATES = [
  { id: 'card', label: 'Card' },
  { id: 'fade-bold', label: 'Fade Bold' },
  { id: 'fade-minimal', label: 'Fade Minimal' },
];

const PREVIEW_MAX_W = 420;

// Font stacks. Real webfonts first, system fallbacks after — the fallback
// only ever shows for the single frame before document.fonts resolves.
const F_DISPLAY = `'Space Grotesk', 'Helvetica Neue', Arial, sans-serif`;
const F_MONO = `'JetBrains Mono', 'Courier New', monospace`;
const F_HERO = `'Archivo Black', 'Helvetica Neue', Arial, sans-serif`;

// --------------------------------------------------------------------------
// Canvas drawing helpers
// --------------------------------------------------------------------------
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function drawSpacedText(ctx, text, x, y, spacing, align = 'left') {
  const widths = [...text].map((ch) => ctx.measureText(ch).width);
  const total = widths.reduce((a, b) => a + b, 0) + spacing * (text.length - 1);
  let cx = align === 'center' ? x - total / 2 : align === 'right' ? x - total : x;
  const prevAlign = ctx.textAlign;
  ctx.textAlign = 'left';
  for (let i = 0; i < text.length; i++) {
    ctx.fillText(text[i], cx, y);
    cx += widths[i] + spacing;
  }
  ctx.textAlign = prevAlign;
}

function wrapText(ctx, text, maxWidth, maxLines = 2) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines.slice(0, maxLines);
}

function drawBackgroundCover(ctx, W, H, img) {
  if (!img) {
    const g = ctx.createLinearGradient(0, 0, 0, H);
    g.addColorStop(0, '#111113');
    g.addColorStop(1, '#000000');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    return;
  }
  const ir = img.width / img.height;
  const cr = W / H;
  let dw, dh, dx, dy;
  if (ir > cr) {
    dh = H; dw = H * ir; dx = (W - dw) / 2; dy = 0;
  } else {
    dw = W; dh = W / ir; dx = 0; dy = (H - dh) / 2;
  }
  ctx.drawImage(img, dx, dy, dw, dh);
}

// Brand mark — radial-gradient green orb + "Traders Spring Group" wordmark,
// same colors used across the app's emails/UI. Drawn from scratch every time
// so the logo never depends on an external asset being present.
function drawLogo(ctx, x, y, size) {
  const r = size / 2;
  const grad = ctx.createRadialGradient(x + r * 0.3, y - r * 0.3, r * 0.1, x, y, r);
  grad.addColorStop(0, '#7dffb3');
  grad.addColorStop(0.55, '#00ff66');
  grad.addColorStop(1, '#00993d');
  ctx.save();
  ctx.shadowColor = 'rgba(0,255,102,0.55)';
  ctx.shadowBlur = size * 0.45;
  ctx.beginPath();
  ctx.arc(x, y, r, 0, Math.PI * 2);
  ctx.fillStyle = grad;
  ctx.fill();
  ctx.restore();

  ctx.font = `600 ${size * 1.05}px ${F_DISPLAY}`;
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'left';
  let tx = x + r + size * 0.55;
  ctx.fillStyle = '#ffffff';
  ctx.fillText('Traders ', tx, y);
  tx += ctx.measureText('Traders ').width;
  ctx.fillStyle = '#00ff66';
  ctx.fillText('Spring', tx, y);
  tx += ctx.measureText('Spring').width;
  ctx.fillStyle = '#ffffff';
  ctx.fillText(' Group', tx, y);
}

// --------------------------------------------------------------------------
// Template renderers. W/H are the ACTUAL target pixel dimensions — the same
// function draws correctly whether called for a small live preview or the
// full 4K export, since every size is derived from W (never a fixed px value).
// --------------------------------------------------------------------------
function drawCard(ctx, W, H, d) {
  drawBackgroundCover(ctx, W, H, d.bgImg);
  const u = W * 0.022;
  const padX = W * 0.07;
  const cardX = padX;
  const cardW = W - padX * 2;

  const rows = d.rows.filter((r) => r.label || r.value);
  const hasHighlight = d.bigNumber || d.bigSuffix || d.codeText;

  const logoRowH = u * 3.4;
  const headingH = d.heading ? u * 4.4 : 0;
  const rowH = u * 2.5;
  const rowsH = rows.length * rowH;
  const highlightH = hasHighlight ? u * 7 : 0;
  const padTop = u * 2.6;
  const padBottom = u * 2.8;
  const cardH = Math.min(H * 0.88, padTop + logoRowH + headingH + rowsH + highlightH + padBottom);
  const cardY = (H - cardH) / 2;

  ctx.fillStyle = 'rgba(4,4,5,0.88)';
  roundRect(ctx, cardX, cardY, cardW, cardH, u * 1.3);
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,255,102,0.35)';
  ctx.lineWidth = Math.max(2, u * 0.1);
  roundRect(ctx, cardX, cardY, cardW, cardH, u * 1.3);
  ctx.stroke();
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, W, H);
  ctx.clip();
  const topGlow = ctx.createLinearGradient(0, 0, 0, u * 3);
  topGlow.addColorStop(0, 'rgba(0,255,102,0.9)');
  topGlow.addColorStop(1, 'rgba(0,255,102,0)');
  ctx.fillStyle = topGlow;
  ctx.fillRect(cardX, cardY, cardW, u * 0.12);
  ctx.restore();

  const innerX = cardX + u * 2.4;
  const innerW = cardW - u * 4.8;
  let cy = cardY + padTop + u * 1.3;

  drawLogo(ctx, innerX + u * 1, cy, u * 2);
  if (d.eyebrow) {
    ctx.font = `700 ${u * 1.0}px ${F_MONO}`;
    ctx.fillStyle = '#ffb020';
    ctx.textBaseline = 'middle';
    drawSpacedText(ctx, d.eyebrow.toUpperCase(), innerX + innerW, cy, u * 0.12, 'right');
  }
  cy += logoRowH - u * 1.3;

  if (d.heading) {
    ctx.font = `700 ${u * 2.5}px ${F_DISPLAY}`;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    const lines = wrapText(ctx, d.heading, innerW, 2);
    lines.forEach((ln, i) => ctx.fillText(ln, innerX, cy + u * 2.2 + i * u * 2.9));
    cy += headingH;
  }

  rows.forEach((row, i) => {
    const ry = cy + i * rowH + rowH * 0.65;
    ctx.font = `600 ${u * 0.95}px ${F_MONO}`;
    ctx.fillStyle = '#a1a1aa';
    ctx.textAlign = 'left';
    drawSpacedText(ctx, (row.label || '').toUpperCase(), innerX, ry, u * 0.08, 'left');
    ctx.font = `700 ${u * 1.25}px ${F_DISPLAY}`;
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'right';
    ctx.fillText(row.value || '', innerX + innerW, ry);
    if (i < rows.length - 1) {
      ctx.strokeStyle = 'rgba(255,255,255,0.08)';
      ctx.lineWidth = Math.max(1, u * 0.04);
      ctx.beginPath();
      ctx.moveTo(innerX, cy + (i + 1) * rowH);
      ctx.lineTo(innerX + innerW, cy + (i + 1) * rowH);
      ctx.stroke();
    }
  });
  cy += rowsH;

  if (hasHighlight) {
    if (rows.length) {
      ctx.strokeStyle = 'rgba(255,255,255,0.1)';
      ctx.lineWidth = Math.max(1, u * 0.05);
      ctx.beginPath();
      ctx.moveTo(innerX, cy + u * 0.6);
      ctx.lineTo(innerX + innerW, cy + u * 0.6);
      ctx.stroke();
    }
    const hy = cy + u * 4.3;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    let hx = innerX;
    if (d.bigNumber) {
      ctx.font = `400 ${u * 4.0}px ${F_HERO}`;
      ctx.fillStyle = '#ffb020';
      ctx.fillText(d.bigNumber, hx, hy);
      hx += ctx.measureText(d.bigNumber).width + u * 0.6;
    }
    if (d.bigSuffix) {
      ctx.font = `400 ${u * 2.8}px ${F_HERO}`;
      ctx.fillStyle = '#ffffff';
      ctx.fillText(d.bigSuffix, hx, hy);
    }
    if (d.codeText) {
      const pillY = hy + u * 1.1;
      ctx.font = `700 ${u * 0.85}px ${F_MONO}`;
      const codeW = ctx.measureText(d.codeText).width;
      const pillPadX = u * 1.1;
      const pillW = codeW + pillPadX * 2 + u * 3.2;
      const pillH = u * 2.1;
      ctx.font = `600 ${u * 0.8}px ${F_MONO}`;
      ctx.fillStyle = '#a1a1aa';
      ctx.textBaseline = 'middle';
      ctx.fillText('USE CODE', innerX, pillY + pillH / 2);
      const pillX = innerX + ctx.measureText('USE CODE').width + u * 0.8;
      ctx.fillStyle = '#ffb020';
      roundRect(ctx, pillX, pillY, pillW, pillH, pillH / 2);
      ctx.fill();
      ctx.font = `700 ${u * 0.95}px ${F_DISPLAY}`;
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'center';
      ctx.fillText(d.codeText.toUpperCase(), pillX + pillW / 2, pillY + pillH / 2 + u * 0.03);
      ctx.textAlign = 'left';
    }
  }
}

function drawFade(ctx, W, H, d, { bold }) {
  drawBackgroundCover(ctx, W, H, d.bgImg);
  const u = W * 0.022;
  const padX = W * 0.07;

  const gradStart = bold ? H * 0.32 : H * 0.72;
  const g = ctx.createLinearGradient(0, gradStart, 0, H);
  g.addColorStop(0, 'rgba(0,0,0,0)');
  g.addColorStop(1, bold ? 'rgba(0,0,0,0.92)' : 'rgba(0,0,0,0.78)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // logo — top-left for the bold layout, resting just above the fade for minimal
  const logoSize = bold ? u * 2.2 : u * 1.9;
  const logoY = bold ? padX + logoSize : H * (0.72 - 0.05);
  drawLogo(ctx, padX + logoSize / 2, logoY, logoSize);

  let cy = H - (bold ? H * 0.17 : H * 0.11);
  const maxTextW = W - padX * 2;

  if (d.eyebrow) {
    ctx.font = `700 ${u * (bold ? 1.05 : 0.9)}px ${F_MONO}`;
    ctx.fillStyle = '#ffb020';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    drawSpacedText(ctx, d.eyebrow.toUpperCase(), padX, cy, u * 0.12, 'left');
    cy += u * (bold ? 2.6 : 2.0);
  }

  if (d.heading) {
    ctx.font = `700 ${u * (bold ? 2.6 : 1.9)}px ${F_DISPLAY}`;
    ctx.fillStyle = '#ffffff';
    const lines = wrapText(ctx, d.heading, maxTextW, 2);
    lines.forEach((ln) => { ctx.fillText(ln, padX, cy); cy += u * (bold ? 3.1 : 2.3); });
    cy += u * 0.4;
  }

  const hasHighlight = d.bigNumber || d.bigSuffix;
  if (hasHighlight) {
    const numSize = bold ? u * 5.0 : u * 3.4;
    const sufSize = bold ? u * 3.5 : u * 2.4;
    ctx.textAlign = 'left';
    let hx = padX;
    if (d.bigNumber) {
      ctx.font = `400 ${numSize}px ${F_HERO}`;
      ctx.fillStyle = '#ffb020';
      ctx.fillText(d.bigNumber, hx, cy);
      hx += ctx.measureText(d.bigNumber).width + u * 0.6;
    }
    if (d.bigSuffix) {
      ctx.font = `400 ${sufSize}px ${F_HERO}`;
      ctx.fillStyle = '#ffffff';
      ctx.fillText(d.bigSuffix, hx, cy);
    }
    cy += bold ? u * 2.2 : u * 1.6;
  }

  if (d.codeText) {
    const pillH = u * (bold ? 2.1 : 1.7);
    ctx.font = `600 ${u * (bold ? 0.85 : 0.72)}px ${F_MONO}`;
    ctx.fillStyle = '#a1a1aa';
    ctx.textBaseline = 'middle';
    ctx.fillText('Use code', padX, cy + pillH / 2);
    const labelW = ctx.measureText('Use code').width;
    const codeFont = `700 ${u * (bold ? 0.95 : 0.8)}px ${F_DISPLAY}`;
    ctx.font = codeFont;
    const codeW = ctx.measureText(d.codeText.toUpperCase()).width;
    const pillW = codeW + u * 2.4;
    const pillX = padX + labelW + u * 0.8;
    ctx.fillStyle = '#ffb020';
    roundRect(ctx, pillX, cy, pillW, pillH, pillH / 2);
    ctx.fill();
    ctx.fillStyle = '#000000';
    ctx.textAlign = 'center';
    ctx.fillText(d.codeText.toUpperCase(), pillX + pillW / 2, cy + pillH / 2 + u * 0.03);
    ctx.textAlign = 'left';
  }
}

function drawScene(ctx, W, H, template, data) {
  ctx.clearRect(0, 0, W, H);
  if (template === 'card') drawCard(ctx, W, H, data);
  else if (template === 'fade-bold') drawFade(ctx, W, H, data, { bold: true });
  else drawFade(ctx, W, H, data, { bold: false });
}

// --------------------------------------------------------------------------
export default function AdminImageGenerator() {
  const [format, setFormat] = useState('post');
  const [template, setTemplate] = useState('card');
  const [bgImg, setBgImg] = useState(null);
  const [bgName, setBgName] = useState('');
  const [eyebrow, setEyebrow] = useState('LIMITED TIME OFFER');
  const [heading, setHeading] = useState('$10K ONE-STEP PROGRAM');
  const [rows, setRows] = useState([
    { label: 'Evaluation', value: '1 Step · 8%' },
    { label: 'Profit Split', value: '80%' },
    { label: 'Max Drawdown', value: '$800 (8%)' },
  ]);
  const [bigNumber, setBigNumber] = useState('45');
  const [bigSuffix, setBigSuffix] = useState('% OFF');
  const [codeText, setCodeText] = useState('TSGBACK');
  const [downloading, setDownloading] = useState(false);
  const [fontsReady, setFontsReady] = useState(false);

  const previewRef = useRef(null);
  const fileInputRef = useRef(null);

  // Canvas does NOT wait for webfonts on its own — if we draw before these
  // resolve, text silently renders in the fallback system font forever
  // (canvas doesn't repaint itself when a font finishes loading). Load them
  // explicitly, then let the preview effect re-run once `fontsReady` flips.
  useEffect(() => {
    let cancelled = false;
    const specs = [
      '500 48px "Space Grotesk"', '600 48px "Space Grotesk"', '700 48px "Space Grotesk"',
      '500 48px "JetBrains Mono"', '600 48px "JetBrains Mono"', '700 48px "JetBrains Mono"',
      '400 48px "Archivo Black"',
    ];
    Promise.all(specs.map((s) => document.fonts.load(s)))
      .then(() => document.fonts.ready)
      .then(() => { if (!cancelled) setFontsReady(true); })
      .catch(() => { if (!cancelled) setFontsReady(true); }); // degrade to system fonts rather than block forever
    return () => { cancelled = true; };
  }, []);

  const buildData = useCallback(() => ({
    bgImg, eyebrow, heading, rows, bigNumber, bigSuffix, codeText,
  }), [bgImg, eyebrow, heading, rows, bigNumber, bigSuffix, codeText]);

  // Live preview — small canvas, same draw functions as the full export.
  useEffect(() => {
    const preset = FORMATS[format];
    const ratio = preset.h / preset.w;
    const pw = PREVIEW_MAX_W;
    const ph = Math.round(pw * ratio);
    const canvas = previewRef.current;
    if (!canvas) return;
    canvas.width = pw;
    canvas.height = ph;
    const ctx = canvas.getContext('2d');
    drawScene(ctx, pw, ph, template, buildData());
  }, [format, template, buildData, fontsReady]);

  const onFilePicked = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => setBgImg(img);
      img.src = String(reader.result || '');
    };
    reader.readAsDataURL(file);
    setBgName(file.name);
  };

  const addRow = () => setRows((r) => [...r, { label: '', value: '' }]);
  const removeRow = (i) => setRows((r) => r.filter((_, idx) => idx !== i));
  const updateRow = (i, key, val) => setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));

  const handleDownload = async () => {
    setDownloading(true);
    await document.fonts.ready; // guarantee the exported file never uses a fallback font
    setTimeout(() => {
      const preset = FORMATS[format];
      const off = document.createElement('canvas');
      off.width = preset.w;
      off.height = preset.h;
      const ctx = off.getContext('2d');
      drawScene(ctx, preset.w, preset.h, template, buildData());
      off.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `tsg-${template}-${format}-${Date.now()}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 3000);
        setDownloading(false);
      }, 'image/png', 1.0);
    }, 30); // let the "Generating…" state paint before the (brief) 4K render blocks the thread
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6">
      {/* ---- Controls ---- */}
      <div className="space-y-5">
        <div>
          <h1 className="font-display text-3xl">Social image generator</h1>
          <p className="text-xs text-zinc-500 mt-1">Build a branded promo image and export it at 4K for Instagram/Telegram/X.</p>
        </div>

        <div className="card-tech rounded-lg p-5">
          <div className="label-sm mb-2">Format</div>
          <div className="flex gap-2 flex-wrap">
            {Object.entries(FORMATS).map(([key, f]) => (
              <button key={key} onClick={() => setFormat(key)}
                className={`btn-sm ${format === key ? 'btn-primary' : 'btn-ghost'}`}
                data-testid={`format-${key}`}>
                {f.label}
              </button>
            ))}
          </div>
          <div className="label-sm mt-4 mb-2">Template</div>
          <div className="flex gap-2 flex-wrap">
            {TEMPLATES.map((t) => (
              <button key={t.id} onClick={() => setTemplate(t.id)}
                className={`btn-sm ${template === t.id ? 'btn-primary' : 'btn-ghost'}`}
                data-testid={`template-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="card-tech rounded-lg p-5">
          <div className="label-sm mb-2">Background image</div>
          {bgImg ? (
            <div className="flex items-center justify-between gap-3 border border-white/10 rounded-md p-3">
              <div className="text-xs text-zinc-400 truncate">{bgName}</div>
              <button className="text-zinc-500 hover:text-white" onClick={() => { setBgImg(null); setBgName(''); if (fileInputRef.current) fileInputRef.current.value = ''; }}>
                <X size={14} />
              </button>
            </div>
          ) : (
            <label htmlFor="bg-file" className="flex items-center justify-center gap-2 border border-dashed border-white/15 bg-white/[0.02] hover:bg-white/[0.04] rounded-md px-4 py-6 cursor-pointer">
              <Upload size={16} className="text-zinc-400" />
              <span className="text-sm">Choose an image <span className="text-zinc-500">· JPG/PNG</span></span>
            </label>
          )}
          <input id="bg-file" ref={fileInputRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => onFilePicked(e.target.files?.[0])} />
        </div>

        <div className="card-tech rounded-lg p-5 space-y-3">
          <div className="label-sm">Text</div>
          <div>
            <div className="label-xs mb-1">Eyebrow tag</div>
            <input className="input-tech" value={eyebrow} onChange={(e) => setEyebrow(e.target.value)} placeholder="LIMITED TIME OFFER" />
          </div>
          <div>
            <div className="label-xs mb-1">Heading</div>
            <input className="input-tech" value={heading} onChange={(e) => setHeading(e.target.value)} placeholder="$10K ONE-STEP PROGRAM" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-xs mb-1">Big number</div>
              <input className="input-tech" value={bigNumber} onChange={(e) => setBigNumber(e.target.value)} placeholder="45" />
            </div>
            <div>
              <div className="label-xs mb-1">Big suffix</div>
              <input className="input-tech" value={bigSuffix} onChange={(e) => setBigSuffix(e.target.value)} placeholder="% OFF" />
            </div>
          </div>
          <div>
            <div className="label-xs mb-1">Promo code (optional)</div>
            <input className="input-tech" value={codeText} onChange={(e) => setCodeText(e.target.value)} placeholder="TSGBACK" />
          </div>
        </div>

        {template === 'card' && (
          <div className="card-tech rounded-lg p-5">
            <div className="flex items-center justify-between mb-2">
              <div className="label-sm">Detail rows</div>
              <button className="btn-ghost btn-sm" onClick={addRow}><Plus size={12} className="inline mr-1" /> Add row</button>
            </div>
            <div className="space-y-2">
              {rows.map((row, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input className="input-tech flex-1" placeholder="Label" value={row.label} onChange={(e) => updateRow(i, 'label', e.target.value)} />
                  <input className="input-tech flex-1" placeholder="Value" value={row.value} onChange={(e) => updateRow(i, 'value', e.target.value)} />
                  <button className="text-zinc-500 hover:text-[#ff3366]" onClick={() => removeRow(i)}><X size={14} /></button>
                </div>
              ))}
              {rows.length === 0 && <div className="text-xs text-zinc-500">No rows yet — add one above.</div>}
            </div>
          </div>
        )}

        <button className="btn-primary w-full justify-center" onClick={handleDownload} disabled={downloading} data-testid="download-image">
          <Download size={14} /> {downloading ? 'Generating 4K image…' : `Download 4K PNG (${FORMATS[format].w}×${FORMATS[format].h})`}
        </button>
      </div>

      {/* ---- Preview ---- */}
      <div className="lg:sticky lg:top-6 h-fit">
        <div className="card-tech rounded-lg p-4">
          <div className="label-sm mb-3 flex items-center gap-2"><ImageIcon size={13} /> Live preview</div>
          <div className="rounded-md overflow-hidden border border-white/10 bg-black flex items-center justify-center">
            <canvas ref={previewRef} className="block max-w-full h-auto" />
          </div>
          <div className="text-[11px] text-zinc-500 mt-3 leading-relaxed">
            Preview is scaled down for speed — the download renders fresh at full {FORMATS[format].w}×{FORMATS[format].h} resolution, so text stays sharp regardless of preview size. For best results, use a background photo at least 2000px on its shortest side.
          </div>
        </div>
      </div>
    </div>
  );
}